import React, { useState, useContext } from 'react';
import axios from 'axios';

import Course from '../models/Course';
import CourseStudent, { StudentType } from '../models/Student';
import Submission, {Log} from '../models/Submission';
import utils from '../libs/utils';

export interface Student {
  studentID: StudentType['userId']
  firstName: StudentType['profile']['name']['givenName']
  lastName: StudentType['profile']['name']['familyName']
  fullName: StudentType['profile']['name']['fullName'],
  courses: Course['id'][]
}

export interface StudentPerspectiveContextState {
  loadData: () => Promise<any>
  getStudentCourses: (id: string) => Course[]
  getCourseById: (id: string) => Course|null
  getStudentById: (id: string) => Student|null
  hasNewSubmission: (courseID: string, studentID: string, assignmentID: string) => boolean
  isLoaded: boolean
  isLoading: boolean
  errorMessage: string
  setErrorMessage: (a: string) => any
  students: Student[]
  courses: Course[]
}

export const StudentPerspectiveContext = React.createContext<StudentPerspectiveContextState>({
  loadData: async () => {},
  getStudentCourses: () => [],
  getCourseById: () => null,
  getStudentById: () => null,
  hasNewSubmission: () => false,
  isLoaded: false,
  isLoading: false,
  errorMessage: '',
  setErrorMessage: () => null,
  students: [],
  courses: []
});

export const StudentPerspectiveProvider = (props: React.ComponentProps<any>) => {
  const [ isLoaded, setIsLoaded ] = useState<boolean>(false);
  const [ isLoading, setIsLoading ] = useState<boolean>(false);
  const [ errorMessage, setErrorMessage ] = useState<string>('');
  const [ courses, setCourses ] = useState<Course[]>([]);
  const [ students, setStudents ] = useState<Student[]>([]);
  const [ submissionLogs, setSubmissionLogs ] = useState([] as Log[]);

  const loadData = async (force = false) => {
    if (isLoaded && !force)
      return;

    try {
      setIsLoading(true);
      setErrorMessage('');
      const { data: registeredCourses } = await axios.get('/admin/course-instances');
      const studentCourses = await Course.getCourses();
      const _courses =  studentCourses.filter(a => (registeredCourses || []).indexOf(a.id) >= 0);
      const _courseStudents =  await Promise.all(_courses.map(a => CourseStudent.find({courseId: a.id})));
      const _submissionLogs = await Submission.getSubmissionLogs();

      // extract students
      const studentMap = new Map<Student['studentID'], Student>();
      for (const _studentList of _courseStudents) {
        for (const _student of _studentList) {
          const _studentCourses = studentMap.get(_student.userId)?.courses || [];
          studentMap.set(_student.userId, {
            studentID: _student.userId,
            firstName: _student.profile.name.givenName,
            lastName: _student.profile.name.familyName,
            fullName: _student.profile.name.fullName,
            courses: [..._studentCourses, _student.courseId]
          });
        }
      }

      setCourses(_courses);
      setStudents(Array.from(studentMap, a => a[1]));
      setSubmissionLogs(_submissionLogs);
      setIsLoaded(true);
    }
    catch (e) {
      setErrorMessage(utils.getRequestErrorMessage(e));
    }
    finally {
      setIsLoading(false);
    }
  };

  const getStudentCourses = (studentId: string): Course[] => {
    return courses.filter(course => {
      for (const student of students) {
        if (student.studentID === studentId) {
          for (const courseId of student.courses) {
            if (courseId === course.id) {
              return true;
            }
          }
        }
      }
      return false;
    });
  };

  const getCourseById = (courseId: string): Course|null => {
    for (let course of courses) {
      if (course.id === courseId)
        return course;
    }
    return null;
  };

  const getStudentById = (studentId: string): Student|null => {
    for (let student of students) {
      if (student.studentID === studentId)
        return student;
    }
    return null;
  };

  const hasNewSubmission = (courseID: string, studentID: string, assignmentID: string): boolean => {
    return submissionLogs.some(
      (log: Log): Boolean =>
        log.courseID === courseID &&
        log.personID === studentID &&
        log.assignmentID === assignmentID
    );
  };

  return (
    <StudentPerspectiveContext.Provider
      value={{
        loadData,
        getStudentCourses,
        getCourseById,
        getStudentById,
        hasNewSubmission,
        isLoaded,
        isLoading,
        errorMessage,
        setErrorMessage,
        students,
        courses,
      }}>
      {props.children}
    </StudentPerspectiveContext.Provider>
  );
};

export const useStudentPerspective = (): StudentPerspectiveContextState => useContext(StudentPerspectiveContext);
